// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { BootstrapButton, BootstrapImage } from '../../dsl/front';
import { AbstractModuleUi } from '../_abstract/ui';
import { ImageRatio } from '../_contracts';
import { prepareImage } from '../_filters';
import { HeroVariant } from './Hero.contracts';
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
let HeroModuleUi = class HeroModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        /**
         * Image to be rendered in the background.
         */
        this.backgroundImage = null;
        /**
         * Badge/logo image to be rendered above the large text.
         */
        this.badgeImage = null;
        /**
         * @private
         */
        this.imageAttrId = this.$id() + 'image';
        /**
         * @inheritDoc
         */
        this.imageRatios = {
            desktop: ImageRatio.HeroDesktop,
            mobile: ImageRatio.HeroMobile
        };
        /**
         * Height of the navbar (in px).
         */
        this.navbarHeight = 0;
    }
    /**
     * URL of the video to be played in the background.
     */
    get backgroundVideo() {
        if (this.module.content.backgroundObj === null ||
            this.module.content.backgroundObj.video === null) {
            return undefined;
        }
        const { desktop: desktopSrc, mobile: mobileSrc } = this.module.content.backgroundObj.video;
        if (desktopSrc === null && mobileSrc === null) {
            return undefined;
        }
        if (typeof window === 'undefined') {
            return mobileSrc !== null
                ? mobileSrc
                : desktopSrc !== null
                    ? desktopSrc
                    : undefined;
        }
        const isMobile = window.innerWidth >= 992;
        if (isMobile) {
            return mobileSrc !== null ? mobileSrc : undefined;
        }
        else {
            return desktopSrc !== null ? desktopSrc : undefined;
        }
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        await this.resolveBackgroundImage();
        await this.resolveBadgeImage();
    }
    /**
     * Determines whether the module has any background media attached.
     */
    get hasBackgroundMedia() {
        return this.module.content.backgroundObj !== null;
    }
    /**
     * Determines whether the hero variant is Standard.
     */
    get isStandardVariant() {
        return (this.module.content.variant || HeroVariant.Standard) === HeroVariant.Standard;
    }
    /**
     * Determines whether the hero variant is Premium.
     */
    get isPremiumVariant() {
        return this.module.content.variant === HeroVariant.Premium;
    }
    /**
     * Processes the large text to wrap text between asterisks with span tags.
     * Example: "to jest *tekst* przykładowy" becomes "to jest <span>tekst</span> przykładowy"
     */
    get processedTextLarge() {
        if (!this.module.content.textLarge) {
            return '';
        }
        return this.module.content.textLarge.replace(/\*([^*]+)\*/g, '<span>$1</span>');
    }
    /**
     * Processes the small text to wrap text between asterisks with span tags.
     * Example: "to jest *tekst* przykładowy" becomes "to jest <span>tekst</span> przykładowy"
     */
    get processedTextSmall() {
        if (!this.module.content.textSmall) {
            return '';
        }
        return this.module.content.textSmall.replace(/\*([^*]+)\*/g, '<span>$1</span>');
    }
    /**
     * Determines whether the hero has a badge image to display.
     */
    get hasBadge() {
        return this.module.content.badge !== null && this.badgeImage !== null;
    }
    /**
     * Handles the `@loadeddata` event on the `<video>` element.
     */
    onVideoLoadedData() {
        this.onReady();
    }
    /**
     * Scrolls to the top of the `<section id="form">`.
     */
    scrollToForm() {
        if (typeof this.scrollTarget !== 'string') {
            return;
        }
        if (this.scrollTarget[0] === '/') {
            this.$router.push(this.scrollTarget);
            return;
        }
        const scrollOffset = window.pageYOffset;
        const formEl = document.getElementById(this.scrollTarget);
        if (formEl === null)
            return;
        const formPosition = formEl.getBoundingClientRect().top;
        const top = scrollOffset + formPosition - this.navbarHeight;
        window.scrollTo({ top });
    }
    /**
     * Observes the attributes of the `.hero-module__bg-image` element
     *
     * @recursive
     */
    observeBackgroundImageAttributes() {
        if (this.module.content.backgroundObj === null) {
            this.onReady();
            return;
        }
        const backgroundImageEl = document.getElementById(this.imageAttrId);
        if (backgroundImageEl === null) {
            setTimeout(() => {
                this.observeBackgroundImageAttributes();
            }, 100);
        }
        else {
            // Create the observer.
            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    // React only if the mutation involves the element's attributes
                    // and if the affected attribute is the `[lazy]` attribute.
                    if (mutation.type === 'attributes' && mutation.attributeName === 'lazy') {
                        // Fetch the current value of the `[lazy]` attribute.
                        const lazy = backgroundImageEl.getAttribute('lazy');
                        // If the `[lazy]` attribute has the value of `loaded`,
                        // reveal the module's contents and disconnect the observer.
                        if (lazy === 'loaded') {
                            this.onReady();
                            observer.disconnect();
                        }
                    }
                });
            });
            // Make the observer observe the target element.
            observer.observe(backgroundImageEl, {
                attributes: true
            });
        }
    }
    /**
     * ID of the element that is the target of the scroll.
     */
    get scrollTarget() {
        if (this.module.content.button === null) {
            return undefined;
        }
        return this.module.content.button.target;
    }
    /**
     * Reads the height of the navbar.
     */
    readNavbarHeight() {
        const navbar = document.querySelector('.navbar');
        this.navbarHeight = navbar !== null ? navbar.getBoundingClientRect().height : 0;
    }
    /**
     * Resolves the background image, if any.
     */
    async resolveBackgroundImage() {
        if (this.module.content.backgroundObj === null ||
            this.module.content.backgroundObj.image === null) {
            return;
        }
        const imageFile = this.relatedService.resolveSync(this.module.content.backgroundObj.image);
        if (!imageFile) {
            return;
        }
        const maxWidth = this.isMobile ? 768 : 1220;
        this.backgroundImage = prepareImage(imageFile, this.imageRatio, maxWidth, this.module.isLazyLoaded);
    }
    /**
     * Resolves the badge image, if any.
     */
    async resolveBadgeImage() {
        var _a;
        if (this.module.content.badge === null) {
            return;
        }
        let imageFile;
        try {
            imageFile = await this.relatedService.resolve(this.module.content.badge);
        }
        catch (e) {
            console.warn('[Hero] Badge image could not be resolved. Please re-select the badge in CMS.', {
                badgeId: (_a = this.module.content.badge) === null || _a === void 0 ? void 0 : _a.value,
                error: e
            });
            // Don't call this.onError(e) to avoid crashing the entire component
            // Just silently fail - badge won't be displayed
            return;
        }
        const maxWidth = this.isMobile ? 200 : 300;
        this.badgeImage = prepareImage(imageFile, ImageRatio.Original, maxWidth, this.module.isLazyLoaded);
    }
};
HeroModuleUi = __decorate([
    Component({
        name: 'HeroModuleUi',
        components: {
            BootstrapButton,
            BootstrapImage
        },
        async created() {
            await this.fetchRelated();
        },
        mounted() {
            this.observeBackgroundImageAttributes();
        },
        template: `
    <header class="hero-module d-flex align-items-center"
            :class="{ '--no-bg-media': !hasBackgroundMedia, '--loaded': !isLoading, '--standard': isStandardVariant, '--premium': isPremiumVariant }">

    <!-- Background video -->
    <video v-if="backgroundVideo" :src="backgroundVideo" autoplay muted playsinline webkit-playsinline
           @loadeddata="onVideoLoadedData"
           class="hero-module__bg-video" ref="video"/>

    <!-- Background image -->
    <BootstrapImage v-else-if="backgroundImage" v-bind="backgroundImage" :id="imageAttrId"
                    class="hero-module__bg-image" @load="onReady"/>

    <!-- Text content -->
    <div class="hero-module__content container">
      <div class="row justify-content-center">
        <div class="col-12 d-flex flex-column justify-content-center align-items-center">
          <!-- Badge -->
          <div v-if="hasBadge" class="hero-module__badge">
            <BootstrapImage v-bind="badgeImage" />
          </div>

          <!-- Heading -->
          <h1 class="hero-module__heading">
            <small v-if="module.content.textSmall" class="hero-module__heading__small"
                   v-html="processedTextSmall"/>
            <span v-if="module.content.textLarge" v-html="processedTextLarge"/>
          </h1>

          <!-- CTA button -->
          <BootstrapButton v-if="module.content.button" class="hero-module__button btn-cta"
                           @click="scrollToForm">
            {{ module.content.button.label }}
          </BootstrapButton>
        </div>
      </div>
    </div>
    <!-- END | Text content -->

    </header>
  `
    })
], HeroModuleUi);
export { HeroModuleUi };
